<template>
  <table class="full-width-table quotes-tab-table">
    <tbody>
      <tr class="table-header">
        <th style="width: 10%">Quote #</th>
        <th style="width: 14%">Created</th>
        <th style="width: 14%">Required</th>
        <th style="width: 14%">Valid To</th>
        <th style="width: 14%">Status</th>
        <th style="width: 16%">Salesperson</th>
        <th style="width: 18%">Job Name</th>
      </tr>
      <tr v-if="!loading && quoteEntries.length > 0" v-for="entry in quoteEntries" :key="entry.QUOTEID" @click="openQuote(entry)" class="quote-entry-row">
        <td>#{{ entry.QUOTENUM }}</td>
        <td>{{ formatDate(entry.CREATEDATE) }}</td>
        <td>{{ formatDate(entry.DATEREQUIRED) }}</td>
        <td>{{ formatDate(entry.VALIDTO) }}</td>
        <td>{{ formatStatus(entry.STATUS) }}</td>
        <td>{{ getDisplayName(entry.ASSIGNEDID) }}</td>
        <td class="job-name-cell">
          <span class="job-name-text">{{ entry.JOBNAME || '' }}</span>
        </td>
      </tr>
      <tr v-if="!loading && quoteEntries.length === 0" class="no-results">
        <td colspan="7">No quotes found</td>
      </tr>
      <tr v-if="loading" class="loading-row">
        <td colspan="7"><img src="../assets/spinner.svg" /></td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { ref, watch, onBeforeUnmount } from 'vue';
import useContactQuotesQuery from '@/composables/useContactQuotesQuery';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useUsersQuery from '@/composables/useUsersQuery';

export default {
  name: 'QuotesTab',
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityid: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const queryContactQuotes = useContactQuotesQuery();
    const { decryptSymbols } = useDecryptSymbols();
    const { allActiveUsers, getUsers } = useUsersQuery();

    getUsers();

    const quoteEntries = ref([]);
    const loading = ref(false);
    let stopResultWatch = null;
    let stopLoadingWatch = null;

    const clearQueryWatches = () => {
      stopResultWatch?.();
      stopLoadingWatch?.();
      stopResultWatch = null;
      stopLoadingWatch = null;
    };

    const applyQuotes = (quotes) => {
      if (!quotes) {
        quoteEntries.value = [];
        return;
      }

      const decryptedEntries = decryptSymbols(quotes);
      quoteEntries.value = decryptedEntries.sort((a, b) => {
        const dateA = parseInt(a.CREATEDATE) || new Date(a.CREATEDATE).getTime();
        const dateB = parseInt(b.CREATEDATE) || new Date(b.CREATEDATE).getTime();
        return dateB - dateA;
      });
    };

    const executeQuery = () => {
      clearQueryWatches();

      if (!props.entityid || props.entityid === 'new') {
        loading.value = false;
        quoteEntries.value = [];
        return;
      }

      const queryParams = {};
      if (props.type === 'company') {
        queryParams.companyid = props.entityid;
      } else if (props.type === 'contact') {
        queryParams.contactid = props.entityid;
      }

      const { result, loading: queryLoading } = queryContactQuotes(queryParams);

      stopLoadingWatch = watch(
        queryLoading,
        (isLoading) => {
          loading.value = !!isLoading;
        },
        { immediate: true }
      );

      stopResultWatch = watch(
        result,
        (newResult) => {
          applyQuotes(newResult?.contactQuotes);
        },
        { immediate: true }
      );
    };

    watch(() => [props.type, props.entityid], executeQuery, { immediate: true });
    onBeforeUnmount(clearQueryWatches);

    return {
      loading,
      quoteEntries,
      executeQuery,
      allActiveUsers,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const timestamp = parseInt(dateString);
      const date = !isNaN(timestamp) ? new Date(timestamp) : new Date(dateString);
      if (date.toString() === 'Invalid Date') return '';
      return date.toLocaleDateString('en-US');
    },
    formatStatus(status) {
      if (!status) return '';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
    getDisplayName(assignedId) {
      if (!assignedId) return 'Unassigned';
      const user = this.allActiveUsers.find((user) => user.USERID === assignedId);
      return user?.DISPLAYNAME || 'Unknown';
    },
    openQuote(quote) {
      this.$router.push({ name: 'quote', params: { quoteid: quote.QUOTEID } });
    },
    addQuote() {
      const query = this.type === 'company' ? { companyid: this.entityid } : { contactid: this.entityid };

      this.$router.push({ name: 'quote', params: { quoteid: 'new' }, query });
    },
  },
};
</script>

<style lang="scss" scoped>
.quotes-tab-table {
  th:first-child,
  td:first-child {
    padding-left: 3.75rem;
  }

  td {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .loading-row {
    td {
      background: white;
    }
  }
}
</style>
